
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Heart, ArrowLeft, Key } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface PatientRegisterFormProps {
  onRegisterSuccess: (user: any) => void;
  switchToLogin: () => void;
  goBack: () => void;
}

export function PatientRegisterForm({ onRegisterSuccess, switchToLogin, goBack }: PatientRegisterFormProps) {
  const { toast } = useToast();
  const { signup } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    dateOfBirth: "",
    address: "",
    emergencyContact: "",
    emergencyPhone: "",
    clinicInvitationCode: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (step < 3) {
      setStep(step + 1);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "Please ensure both password fields match.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const success = await signup(formData.email, formData.password, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        role: 'patient',
        dateOfBirth: formData.dateOfBirth,
        address: formData.address,
        emergencyContact: formData.emergencyContact,
        emergencyPhone: formData.emergencyPhone,
      });

      if (success) {
        // If a clinic invitation code was provided, try to join the clinic
        if (formData.clinicInvitationCode.trim()) {
          console.log('Attempting to join clinic with key:', formData.clinicInvitationCode);
          
          const { data: user } = await supabase.auth.getUser();
          if (user?.user?.id) {
            const { data: joinResult, error: joinError } = await supabase.rpc('join_clinic_with_key', {
              p_invitation_key: formData.clinicInvitationCode.trim().toUpperCase(),
              p_user_id: user.user.id
            });

            if (joinError) {
              console.error('Error joining clinic:', joinError);
              toast({
                title: "Registration Successful",
                description: "Your account has been created, but we couldn't associate you with the clinic. Please contact the clinic for assistance.",
                variant: "destructive",
              });
            } else if (!joinResult) {
              toast({
                title: "Registration Successful",
                description: "Your account has been created, but the clinic invitation code is invalid or expired. Please contact the clinic for a new code.",
                variant: "destructive",
              });
            } else {
              toast({
                title: "Registration Successful",
                description: "Welcome to ClinicaFlow Pro! You've been successfully associated with your clinic. Please check your email to verify your account.",
              });
            }
          }
        } else {
          toast({
            title: "Registration Successful",
            description: "Welcome to ClinicaFlow Pro! Please check your email to verify your account.",
          });
        }
        // The auth context will handle the rest
      } else {
        toast({
          title: "Registration Failed",
          description: "Please check your information and try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: "Registration Failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateStep = () => {
    if (step === 1) {
      return formData.email && formData.password && formData.confirmPassword;
    }
    if (step === 2) {
      return formData.firstName && formData.lastName && formData.phone && formData.dateOfBirth;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={step === 1 ? goBack : () => setStep(step - 1)}
            className="absolute left-4 top-4"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Patient Registration</CardTitle>
          <CardDescription>Step {step} of 3 - Create your patient account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {step === 1 && (
              <>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="patient@email.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Create a strong password"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm your password"
                    required
                  />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="John"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Doe"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    required
                  />
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div>
                  <Label htmlFor="clinicInvitationCode" className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Clinic Invitation Code (Optional)
                  </Label>
                  <Input
                    id="clinicInvitationCode"
                    value={formData.clinicInvitationCode}
                    onChange={(e) => setFormData({ ...formData, clinicInvitationCode: e.target.value })}
                    placeholder="e.g., A1B2C3"
                    className="uppercase font-mono"
                    maxLength={6}
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    Enter the 6-character code provided by your clinic to be automatically associated with them.
                  </p>
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St, City, State 12345"
                  />
                </div>
                <div>
                  <Label htmlFor="emergencyContact">Emergency Contact Name</Label>
                  <Input
                    id="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={(e) => setFormData({ ...formData, emergencyContact: e.target.value })}
                    placeholder="Emergency contact full name"
                  />
                </div>
                <div>
                  <Label htmlFor="emergencyPhone">Emergency Contact Phone</Label>
                  <Input
                    id="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={(e) => setFormData({ ...formData, emergencyPhone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </>
            )}

            <Button 
              type="submit" 
              className="w-full bg-green-600 hover:bg-green-700" 
              disabled={!validateStep() || isLoading}
            >
              {step < 3 ? "Next" : isLoading ? "Creating Account..." : "Create Account"}
            </Button>

            <div className="text-center">
              <Button variant="link" onClick={switchToLogin} className="text-sm">
                Already have an account? Sign in
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
